import { NoImage } from "@assets/constant/index.constant";
import { Badge } from "@components/ui/badge";
import { Button } from "@components/ui/button";
import { Checkbox } from "@components/ui/checkbox";
import { endpoint } from "@services/api.services";
import { METHODS } from "@services/interface.service";
import Tooltip from "@ui/external/components/tooltip";
import { UserProfile } from "@ui/internal/components/card";
import PrintCertificate from "@ui/internal/components/print-certificate";
import {
  formatDateTime,
  isNullCheck,
  showToast,
} from "@utils/helper/index.helper";
import { useHandleAction, usePathname } from "@utils/hook/index.hook";
import { ColumnType } from "@utils/interface/index.interface";
import {
  InvoiceCourseBatchData,
  StudentData,
} from "@utils/interface/journey.interface";
import { usePostData } from "@utils/query/index.query";
import { useMemo } from "react";
import { Link, useSearchParams } from "react-router";
import { getRepeatBatchIds, hasRepeatBatch } from "../helper/repeat.helper";

/* repeat pills are deliberately off the yellow palette the other pills share, so a
   repeated batch is spottable without reading the text */
const REPEAT_PILL =
  "rounded-full border-[#7C3AED] bg-[#F5F3FF] text-[#7C3AED]";
interface CloumnListProps {
  refetch?: () => void;
}
const ColumnList = ({ refetch }: CloumnListProps) => {
  const studentEndpoint = useMemo(() => endpoint["student"], []);
  const pathname = usePathname();
  /* on the masterclass page, name + email + phone share one column to save width —
     on the regular students page they stay as separate columns */
  const isMasterclass = pathname === "masterclass-students";
  const { handleAction } = useHandleAction({
    endpoint: studentEndpoint,
    refetch,
  });

  const { mutate } = usePostData();
  const handleDropout = (data: any) => {
    mutate(
      {
        endpoint: endpoint["student"],
        data,
        method: METHODS.PATCH,
      },
      {
        onSuccess: ({ status }) => {
          refetch && refetch();
          showToast({
            description: `Student dropout updated`,
            type: "success",
            title: "Success",
          });
        },
      },
    );
  };
  const columns: ColumnType<StudentData>[] = useMemo(
    () => [
      {
        name: "ID",
        cell: (row) => (
          <span className="font-semibold text-gray-700">{row?.id}</span>
        ),
        sortable: true,
      },
      {
        name: "Created Date",
        accessKey: "created_date",
        cell: (row: any) => {
          return (
            <span className="font-semibold text-gray-700">
              {row.updated_date
                ? formatDateTime(row?.updated_date)
                : formatDateTime(row?.created_date)}
            </span>
          );
        },
        sortable: true,
      },
      {
        name: isMasterclass ? "Student Details" : "Full Name",
        width: isMasterclass ? "320px" : undefined,

        cell: (row) => (
          <div className="flex flex-col items-start">
            <UserProfile
              className={isMasterclass ? "[&_span]:max-w-[270px]" : undefined}
              userimage={row.professional_image || NoImage}
              userdesignation={row.email ?? row?.user?.email}
              username={row.user.full_name}
            />
            {isMasterclass && (
              <span className="text-gray-600 text-sm ms-[50px] -mt-1">
                {isNullCheck(row.user.phone) as string}
              </span>
            )}
          </div>
        ),
        sortable: false,
      },
      ...(isMasterclass
        ? []
        : [
            {
              name: "Phone Number",
              cell: (row: StudentData) => <>{isNullCheck(row.user.phone)}</>,
              sortable: false,
            },
          ]),
      {
        name: "Course Batch",
        cell: (row) => {
          if (row.is_masterclass)
            return <Badge variant={"warning"}>Masterclass</Badge>;

          /* filtering by a single course_batch narrows this array to one item, so
             there's no sibling to compare against — trust the backend's row-level
             is_repeat (already scoped to that batch) instead of the heuristic */
          const repeats =
            (row.course_batch?.length ?? 0) === 1
              ? row.is_repeat
                ? new Set(row.course_batch!.map((item) => item.id))
                : new Set<number>()
              : getRepeatBatchIds(row.course_batch);
          /* a repeated batch carries no invoice, so say so on the pill itself */
          const label = (item: { id: number; name: string }) =>
            repeats.has(item.id) ? `${item.name} ↻` : item.name;
          const pill = (item: { id: number; name: string }) =>
            repeats.has(item.id) ? (
              <Tooltip
                key={item.id}
                description={`Repeat of a course already taken — no invoice generated for ${item.name}`}
              >
                <Badge variant={"outline"} className={REPEAT_PILL}>
                  {label(item)}
                </Badge>
              </Tooltip>
            ) : (
              <Badge key={item.id} variant={"warning"}>
                {item.name}
              </Badge>
            );

          return (
            <>
              {(row.course_batch?.length ?? 0) > 1 ? (
                <div className="flex gap-2 justify-start w-full">
                  {row.course_batch?.slice(0, 1).map(pill)}
                  <Tooltip
                    description={(row?.course_batch ?? [])
                      .slice(1)
                      .map(label)
                      .join(", ")}
                  >
                    <Badge variant={"warning"}>
                      +{(row.course_batch?.length ?? 0) - 1}
                    </Badge>
                  </Tooltip>
                </div>
              ) : (
                <div className="flex gap-2 ">
                  {row.course_batch?.map(pill) || "N/A"}{" "}
                </div>
              )}
            </>
          );
        },

        sortable: false,
      },
      {
        name: "Batch Repeater",
        cell: (row) => (
          <Tooltip description="Re-enrolled in the same batch">
            <Badge variant={row.is_batch_repeater ? "warning" : "secondary"}>
              {row.is_batch_repeater ? "Batch Repeater" : "Not Repeating"}
            </Badge>
          </Tooltip>
        ),
        sortable: false,
      },
      {
        name: "Course Repeat",
        /* the backend flag is authoritative (it decides which batch skips the
           invoice); the M2M list is only the fallback when it isn't serialised */
        cell: (row) => {
          const isRepeat = row.is_repeat ?? hasRepeatBatch(row.course_batch);
          return (
            <Tooltip description="Taking the same course again in a different batch — the repeated batch gets no invoice">
              <Badge
                variant={"outline"}
                className={isRepeat ? REPEAT_PILL : "rounded-full"}
              >
                {isRepeat ? "Repeat (New Batch)" : "First Time"}
              </Badge>
            </Tooltip>
          );
        },
        sortable: false,
      },
      {
        name: "Course Status",
        cell: (row) => (
          <>
            {(row.course_batch?.length ?? 0) > 1 ? (
              <div className="flex gap-2 ">
                {row.course_batch?.slice(0, 1).map((item) => (
                  <Badge variant={"warning"}>
                    {item.student_course_batch_status}
                  </Badge>
                ))}
                <Tooltip
                  description={(row?.course_batch ?? [])
                    .slice(1)
                    .map((item) => item.student_course_batch_status)
                    .join(", ")}
                >
                  <Badge variant={"warning"}>
                    +{(row.course_batch?.length ?? 0) - 1}
                  </Badge>
                </Tooltip>
              </div>
            ) : (
              <div className="flex gap-2 ">
                {row.course_batch?.map((item) => (
                  <Badge variant={"warning"}>
                    {item.student_course_batch_status}
                  </Badge>
                )) || "N/A"}
              </div>
            )}
          </>
        ),
        sortable: true,
      },
      {
        name: "Payment Status",
        cell: (row) => {
          const allStatuses =
            row.course_batch?.map((item) => isNullCheck(item.payment_status)) ||
            [];

          const firstStatus = allStatuses[0];
          const remainingStatuses = allStatuses.slice(1);

          return (
            <div className="flex gap-2 flex-row items-center">
              {firstStatus ? (
                <Badge variant={"warning"}>{firstStatus as string}</Badge>
              ) : (
                <span className="text-muted-foreground text-sm">N/A</span>
              )}

              {remainingStatuses.length > 0 && (
                <Tooltip description={remainingStatuses.join(", ")}>
                  <Badge variant={"warning"}>+{remainingStatuses.length}</Badge>
                </Tooltip>
              )}
            </div>
          );
        },
        sortable: true,
      },
      {
        name: "Mark as dropout",
        cell: (row) => (
          <>
            <Checkbox
              className="cursor-pointer"
              checked={!row.status}
              onCheckedChange={() =>
                handleDropout({ id: row.id, status: !row.status })
              }
            />
          </>
        ),
        sortable: true,
      },
      {
        name: "Certification",
        sortable: true,
        cell: (row) => (
          <>
            {row?.all_certificates_created ? (
              "ALL GENERATED"
            ) : (
              <Button
                type="button"
                onClick={() => {
                  handleAction({ type: "confirm-close" });
                  handleAction({ type: "mode-close" });
                  handleAction({ type: "close" });
                  handleAction({ type: "page-close" });
                  handleAction({
                    type: "mode-add",
                    data: { type: "generate", id: row.id },
                  });
                }}
              >
                Generate Certificate
              </Button>
            )}
          </>
        ),
      },
      {
        name: "Actions",
        cell: (row) => (
          <div className="flex items-center gap-3 cursor-pointer font-[500] select-none">
            <button
              type="button"
              className="text-[#1DA077] cursor-pointer"
              onClick={() => {
                handleAction({ type: "confirm-close" });
                handleAction({ type: "mode-close" });
                handleAction({ type: "page-edit", data: row.id });
              }}
              title="Edit"
            >
              Edit
            </button>
            <button
              className="text-[#C33241] cursor-pointer"
              onClick={() => {
                handleAction({ type: "confirm-close" });
                handleAction({ type: "modal-close" });
                handleAction({ type: "close" });
                handleAction({
                  type: "confirm",
                  data: { type: "delete", id: row.id },
                });
              }}
              title="Delete"
            >
              Delete
            </button>
            <PrintCertificate studentId={row.id} />
          </div>
        ),
        sortable: false,
      },
    ],
    [handleAction, isMasterclass],
  );

  return columns;
};

const CoursesColumnDetails = ({ studentId }: { studentId: string }) => {
  const { handleAction } = useHandleAction({
    endpoint: endpoint["remove-coursebatch"],
  });
  const [searchParams, setSearchParams] = useSearchParams();
  /* a repeated batch is never invoiced, so it has no amounts to show — without this
     the null due_amount reads as 0 and the row would claim "Paid" */
  const isUninvoiced = (row: InvoiceCourseBatchData) =>
    !!row?.is_repeat || !row?.invoice_id;
  const columns: ColumnType<InvoiceCourseBatchData>[] = useMemo(
    () => [
      {
        name: "ID",

        cell: (row) => (
          <span className="font-semibold text-gray-700">{row?.id}</span>
        ),
        sortable: true,
      },
      {
        name: "Course & Batch",
        cell: (row) => <>{row?.course_batch_name}</>,
        sortable: false,
      },
      {
        name: "Total Amount",
        cell: (row) => <>{isUninvoiced(row) ? "—" : isNullCheck(row?.total_amount)}</>,
        sortable: false,
      },
      {
        name: "Paid Amount",
        cell: (row) => (
          <>{isUninvoiced(row) ? "—" : isNullCheck(row?.paid_amount) || "0.00"}</>
        ),
        sortable: false,
      },
      {
        name: "Due Amount",
        cell: (row) => (
          <>{isUninvoiced(row) ? "—" : isNullCheck(row?.due_amount) || "0.00"}</>
        ),
        sortable: false,
      },
      {
        name: "Discount Amount",
        cell: (row) => (
          <>{isUninvoiced(row) ? "—" : isNullCheck(row?.discount) || "0.00"}</>
        ),
        sortable: false,
      },
      {
        name: "Payment Status",
        cell: (row) =>
          isUninvoiced(row) ? (
            <Badge variant={"outline"} className={REPEAT_PILL}>
              Repeat — No Invoice
            </Badge>
          ) : (
            <>{Number(row?.due_amount) == 0 ? "Paid" : "Unpaid"}</>
          ),
        sortable: false,
      },
      {
        name: "Show Payment History",
        cell: () => (
          <>
            {studentId !== "#" ? (
              <Link to={`/invoice?student__in=${studentId}`}>
                <Button>Show Details</Button>
              </Link>
            ) : (
              "Student  Not Created "
            )}
          </>
        ),
        sortable: false,
      },
      {
        name: "Action",
        cell: (row) => (
          <>
            <span
              onClick={(e) => {
                e.preventDefault(); // stop implicit submit
                e.stopPropagation();
                searchParams.set("course_id", String(row?.course_batch_id));
                setSearchParams(searchParams);
              }}
              className="text-red-500 capitalize"
            >
              delete
            </span>
          </>
        ),
        sortable: false,
      },
    ],
    [handleAction],
  );

  return columns;
};

export { ColumnList, CoursesColumnDetails };
